const DEFAULT_SITE_BRAND = {
  name: 'Glowup',
  sub: 'content command center',
  avatar: 'busy business owners',
  avatarShort: 'business owners',
  possessive: 'your business and your customers',
  salesMode: 'direct',
  seoTitle: 'AI Social Media Manager for Small Business | Glowup',
  seoDescription: 'Get five social posts to start, then one new post every week free forever. Upgrade to three or five planned posts each week.',
};

const IRIDIUM_SITE_BRAND = {
  name: 'Iridium',
  sub: 'content engine',
  avatar: 'founders, leaders, and growing teams',
  avatarShort: 'founders',
  possessive: 'your expertise and your market',
  seoTitle: 'AI Social Media Manager for Small Business | Iridium',
  seoDescription: 'Get five social posts to start, then one new post every week free forever. Upgrade to three or five planned posts each week.',
};

const HOST_BRANDS = {
  'glowup.botmo.io': DEFAULT_SITE_BRAND,
  'content.geniusxai.com': {
    name: 'GeniusXAi',
    sub: 'content system',
    productName: 'Content System',
    avatar: 'real estate agents',
    avatarShort: 'agents',
    possessive: 'your listings and your market',
    salesMode: 'reseller',
    salesContactUrl: 'https://calendly.com/office-geniusxai/discovery-ai-demo-call?utm_source=content.geniusxai.com&utm_medium=website&utm_campaign=content_system_managed_launch&utm_content=pricing',
    seoTitle: 'AI Content System for Real Estate Agents | GeniusXAi',
    seoDescription: 'Get five real estate posts to start, then one new post every week free forever. Upgrade to three or five planned posts each week.',
  },
  'content.elevatedaisolutions.com': {
    name: 'Elevated AI',
    sub: 'content engine',
    avatar: 'agency owners and service businesses',
    avatarShort: 'business owners',
    possessive: 'your offer and your market',
    seoTitle: 'AI Social Media Manager for Service Businesses | Elevated AI',
    seoDescription: 'Get five social posts to start, then one new post every week free forever. Upgrade to three or five planned posts each week.',
  },
  'content.irridium.com': IRIDIUM_SITE_BRAND,
  'content.iridium.com': IRIDIUM_SITE_BRAND,
  'content.iridiumprivate.com': IRIDIUM_SITE_BRAND,
  'content.iridiumprivate.com.au': IRIDIUM_SITE_BRAND,
  'iridium.thedylanewing.com': IRIDIUM_SITE_BRAND,
  'iridium-content.vercel.app': IRIDIUM_SITE_BRAND,
};

export const resolveSiteBrand = (hostname = '') => (
  HOST_BRANDS[String(hostname).trim().toLowerCase()] || DEFAULT_SITE_BRAND
);

export const currentSiteBrand = () => resolveSiteBrand(
  typeof window !== 'undefined' ? window.location.hostname : ''
);

export const applyCurrentSiteBrandMetadata = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const brand = currentSiteBrand();
  const pathname = window.location.pathname || '/';
  const isLanding = pathname === '/';
  const productName = brand.productName || 'Content Engine';
  const title = isLanding
    ? (brand.seoTitle || `${brand.name} ${productName}`)
    : `${brand.name} ${productName}`;
  const description = isLanding
    ? (brand.seoDescription || `${brand.name} plans and writes your social media posts.`)
    : `${brand.name} learns your business, plans the week, and writes your content for you. You check it before anything goes out.`;
  const pageUrl = `${window.location.origin}${isLanding ? '/' : pathname}`;

  const setMeta = (selector, attribute, content) => {
    let element = document.head.querySelector(selector);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, selector.match(/["']([^"']+)["']/)?.[1] || '');
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  const setCanonical = (href) => {
    let element = document.head.querySelector('link[rel="canonical"]');
    if (!href) {
      element?.remove();
      return;
    }
    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
      document.head.appendChild(element);
    }
    element.setAttribute('href', href);
  };

  const setProductSchema = (data) => {
    let element = document.head.querySelector('script[data-site-product-schema="true"]');
    if (!data) {
      element?.remove();
      return;
    }
    if (!element) {
      element = document.createElement('script');
      element.setAttribute('type', 'application/ld+json');
      element.setAttribute('data-site-product-schema', 'true');
      document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
  };

  document.title = title;
  setMeta('meta[name="description"]', 'name', description);
  setMeta('meta[name="robots"]', 'name', isLanding ? 'index, follow, max-image-preview:large' : 'noindex, nofollow');
  setMeta('meta[property="og:title"]', 'property', title);
  setMeta('meta[property="og:description"]', 'property', description);
  setMeta('meta[property="og:type"]', 'property', 'website');
  setMeta('meta[property="og:site_name"]', 'property', brand.name);
  setMeta('meta[property="og:url"]', 'property', pageUrl);
  setMeta('meta[name="twitter:title"]', 'name', title);
  setMeta('meta[name="twitter:description"]', 'name', description);
  setCanonical(isLanding ? pageUrl : '');
  setProductSchema(isLanding ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${pageUrl}#website`,
        name: brand.name,
        url: pageUrl,
        description,
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${pageUrl}#software`,
        name: `${brand.name} ${productName}`,
        url: pageUrl,
        description,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        audience: {
          '@type': 'Audience',
          audienceType: brand.avatar,
        },
        featureList: [
          'Weekly social media plan',
          'Five posts ready to review',
          'Voice profile and strategy sheet',
          'Approval before anything is sent',
        ],
        offers: {
          '@type': 'AggregateOffer',
          url: `${pageUrl}#pricing`,
          priceCurrency: 'USD',
          lowPrice: '0',
          highPrice: '300',
          offerCount: '3',
        },
      },
    ],
  } : null);
};
